import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaExternalLinkAlt, FaClock, FaBox, FaCalculator, FaTruck } from 'react-icons/fa';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import toastService from '@/utils/toastService';

/**
 * Carrito de compras para repuestos mixtos (propios + teknoParts)
 * Maneja diferentes tipos de repuestos y sus tiempos de entrega
 */
const CarritoRepuestosExternos = ({ 
  isOpen, 
  onClose, 
  carrito = [], 
  onUpdateCarrito, 
  onProcederPago,
  mantenimientoId = null 
}) => {
  const [loading, setLoading] = useState(false);
  const [mostrarDesglose, setMostrarDesglose] = useState(false);

  // Calcular totales
  const calcularTotales = () => {
    let totalPropios = 0;
    let totalExternos = 0;
    let totalComisiones = 0;
    let tiempoEntregaMaximo = 0;

    const itemsPropios = carrito.filter(item => !item.esExterno);
    const itemsExternos = carrito.filter(item => item.esExterno);

    // Calcular totales propios
    itemsPropios.forEach(item => {
      totalPropios += item.precio * item.cantidad;
    });

    // Calcular totales teknoParts
    itemsExternos.forEach(item => {
      const subtotal = item.precio * item.cantidad;
      totalExternos += subtotal;
      if (item.comision) {
        totalComisiones += item.comision * item.cantidad;
      }
      if (item.tiempoEntrega > tiempoEntregaMaximo) {
        tiempoEntregaMaximo = item.tiempoEntrega;
      }
    });

    return {
      propios: totalPropios,
      teknoParts: totalExternos,
      comisiones: totalComisiones,
      total: totalPropios + totalExternos,
      cantidadItems: carrito.reduce((acc, item) => acc + item.cantidad, 0),
      tiempoEntregaMaximo,
      itemsPropios: itemsPropios.length,
      itemsExternos: itemsExternos.length
    };
  };

  const totales = calcularTotales();

  const updateCantidad = (itemId, nuevaCantidad) => {
    if (nuevaCantidad <= 0) {
      removeFromCarrito(itemId);
      return;
    }

    const nuevoCarrito = carrito.map(item => 
      item.id === itemId 
        ? { ...item, cantidad: nuevaCantidad }
        : item
    );
    
    onUpdateCarrito(nuevoCarrito);
    toastService.success('Cantidad actualizada');
  };

  const removeFromCarrito = (itemId) => {
    const nuevoCarrito = carrito.filter(item => item.id !== itemId);
    onUpdateCarrito(nuevoCarrito);
    toastService.success('Repuesto eliminado del carrito');
  };

  const clearCarrito = () => {
    onUpdateCarrito([]);
    toastService.success('Carrito vaciado');
  };

  const handleProcederPago = async () => {
    if (carrito.length === 0) {
      toastService.error('El carrito está vacío');
      return;
    }

    setLoading(true);
    try {
      // Simular validación de stock y precios
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Llamar función de pago
      onProcederPago({
        items: carrito,
        totales,
        mantenimientoId
      });
      
    } catch (error) {
      toastService.error('Error al proceder con el pago');
    } finally {
      setLoading(false);
    }
  };

  const getEstimacionEntrega = () => {
    if (totales.tiempoEntregaMaximo === 0) {
      return 'Disponible inmediatamente';
    } else if (totales.tiempoEntregaMaximo === 1) {
      return 'Entrega mañana';
    } else {
      return `Entrega en ${totales.tiempoEntregaMaximo} días hábiles`;
    }
  };

  const getProveedorInfo = (item) => {
    if (!item.esExterno || !item.proveedor) return null;
    
    return (
      <div className="flex items-center text-xs text-gray-400 mt-1">
        <img 
          src={item.proveedor.logo} 
          alt={item.proveedor.nombre}
          className="w-4 h-4 rounded mr-1"
        />
        <span>{item.proveedor.nombre}</span>
        {item.tiempoEntrega > 0 && (
          <span className="ml-2 text-yellow-400">
            • {item.tiempoEntrega} día{item.tiempoEntrega > 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Carrito de Repuestos"
      size="6xl"
      className="w-full lg:max-w-6xl"
      headerExtra={
        <div className="flex flex-col items-end gap-1">
          <Badge variant="primary" size="sm">
            {totales.cantidadItems} items
          </Badge>
          {totales.itemsExternos > 0 && (
            <Badge variant="info" size="sm">
              <FaClock className="mr-1" />
              {getEstimacionEntrega()}
            </Badge>
          )}
        </div>
      }
    >
      <div className="flex flex-col lg:flex-row gap-6 max-h-[80vh]">
        {carrito.length === 0 ? (
          // Carrito vacío
          <div className="text-center py-12 w-full">
            <FaShoppingCart className="mx-auto text-6xl text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Tu carrito está vacío</h3>
            <p className="text-gray-400 mb-4">Agrega algunos repuestos para comenzar</p>
            <div className="flex justify-center mb-4">
              <Button variant="primary" onClick={onClose}>
                Explorar Repuestos
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Columna 1: Productos */}
            <div className="flex-1 flex flex-col">
              {/* Header del carrito */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-700">
                <div className="flex items-center gap-4">
                  <h3 className="font-semibold">
                    {totales.cantidadItems} repuesto{totales.cantidadItems > 1 ? 's' : ''} en el carrito
                  </h3>
                  
                  {totales.itemsPropios > 0 && totales.itemsExternos > 0 && (
                    <div className="flex gap-2">
                      <Badge variant="success" size="sm">
                        <FaBox className="mr-1" />
                        {totales.itemsPropios} propios
                      </Badge>
                      <Badge variant="info" size="sm">
                        <FaExternalLinkAlt className="mr-1" />
                        {totales.itemsExternos} teknoParts
                      </Badge>
                    </div>
                  )}
                </div>
                
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={clearCarrito}
                  icon={<FaTrash />}
                >
                  Vaciar
                </Button>
              </div>

              {/* Lista de items */}
              <div className="flex-1 overflow-y-auto space-y-3">
              {carrito.map(item => (
                <Card key={item.id} className="bg-gray-700 border border-gray-600">
                  <CardBody className="p-3">
                    <div className="flex gap-3">
                      {/* Imagen del producto */}
                      <div className="flex-shrink-0">
                        <img 
                          src={item.imagen} 
                          alt={item.nombre}
                          className="w-12 h-12 object-cover rounded"
                        />
                      </div>

                      {/* Información del producto */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 pr-2">
                            <h4 className="font-medium text-white text-sm leading-tight">{item.nombre}</h4>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-gray-400">{item.codigo}</p>
                              <Badge 
                                variant={item.esExterno ? 'info' : 'success'}
                                size="xs"
                              >
                                {item.esExterno ? 'teknoParts' : 'Propio'}
                              </Badge>
                            </div>
                            {getProveedorInfo(item)}
                          </div>
                          
                          <Button
                            variant="danger"
                            size="xs"
                            onClick={() => removeFromCarrito(item.id)}
                            icon={<FaTrash />}
                            className="w-6 h-6 p-0 flex items-center justify-center"
                          />
                        </div>

                        {/* Controles de cantidad y precio */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="secondary"
                              size="xs"
                              onClick={() => updateCantidad(item.id, item.cantidad - 1)}
                              disabled={item.cantidad <= 1}
                              className="w-6 h-6 p-0 flex items-center justify-center text-xs"
                            >
                              -
                            </Button>
                            
                            <span className="w-8 text-center font-medium text-sm">
                              {item.cantidad}
                            </span>
                            
                            <Button
                              variant="secondary"
                              size="xs"
                              onClick={() => updateCantidad(item.id, item.cantidad + 1)}
                              className="w-6 h-6 p-0 flex items-center justify-center text-xs"
                            >
                              +
                            </Button>
                          </div>

                          <div className="text-right">
                            <div className="font-semibold text-white">
                              ${(item.precio * item.cantidad).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                            </div>
                            <div className="text-xs text-gray-400">
                              ${item.precio.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} c/u
                            </div>
                          </div>
                        </div>

                        {/* Desglose de precio para teknoParts */}
                        {item.esExterno && mostrarDesglose && (
                          <div className="mt-3 p-3 bg-gray-900 rounded text-xs">
                            <div className="grid grid-cols-3 gap-2 text-gray-400">
                              <div>
                                <span className="block">Precio proveedor:</span>
                                <span className="text-white font-medium">
                                  ${(item.precioProveedor || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                </span>
                              </div>
                              <div>
                                <span className="block">Comisión:</span>
                                <span className="text-blue-400 font-medium">
                                  ${(item.comision || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                </span>
                              </div>
                              <div>
                                <span className="block">Margen taller:</span>
                                <span className="text-green-400 font-medium">
                                  ${(item.margenTaller || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
              </div>
            </div>

            {/* Columna 2: Resumen del Pedido */}
            <div className="w-full lg:w-96 flex flex-col">

              {/* Resumen de totales */}
            <Card className="bg-gray-700 border-gray-600">
              <CardBody className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold">Resumen del Pedido</h4>
                  
                  {totales.itemsExternos > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMostrarDesglose(!mostrarDesglose)}
                      icon={<FaCalculator />}
                    >
                      {mostrarDesglose ? 'Ocultar' : 'Ver'} Desglose
                    </Button>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  {totales.itemsPropios > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">
                        Repuestos propios ({totales.itemsPropios}):
                      </span>
                      <span>${totales.propios.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                    </div>
                  )}

                  {totales.itemsExternos > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">
                        Repuestos teknoParts ({totales.itemsExternos}):
                      </span>
                      <span>${totales.teknoParts.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                    </div>
                  )}

                  {mostrarDesglose && totales.comisiones > 0 && (
                    <div className="pl-4 space-y-1 text-xs border-l-2 border-blue-500">
                      <div className="flex justify-between text-blue-400">
                        <span>Comisiones incluidas:</span>
                        <span>${totales.comisiones.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                      </div>
                      <p className="text-gray-500">
                        Las comisiones están incluidas en el precio final
                      </p>
                    </div>
                  )}

                  <div className="border-t border-gray-600 pt-2 mt-3">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total:</span>
                      <span className="text-green-400">
                        ${totales.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      </span>
                    </div>
                  </div>
                </div>
              </CardBody>
              </Card>

              {/* Información unificada entre resumen y botones */}
              {(totales.itemsExternos > 0 || mantenimientoId) && (
                <div className="mt-4 p-3 bg-blue-900/20 border border-blue-800/50 rounded">
                  <div className="flex items-start">
                    <FaTruck className="text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-200 mb-1.5 text-sm">Información Importante</h4>
                      <div className="space-y-1 text-xs text-blue-300">
                        {totales.itemsExternos > 0 && (
                          <>
                            <p>• {getEstimacionEntrega()} para repuestos teknoParts</p>
                            <p>• Los repuestos teknoParts se pedirán automáticamente al confirmar el pago</p>
                            <p>• Recibirás notificaciones sobre el estado de la entrega</p>
                          </>
                        )}
                        {totales.itemsPropios > 0 && (
                          <p>• Los repuestos propios están disponibles inmediatamente</p>
                        )}
                        {mantenimientoId && (
                          <p>• Los repuestos se asociarán al mantenimiento #{mantenimientoId}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex flex-col gap-3 mt-6 mb-4">
                <Button
                  variant="primary"
                  onClick={handleProcederPago}
                  loading={loading}
                  className="w-full"
                  icon={<FaShoppingCart />}
                >
                  Proceder al Pago
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={onClose}
                  className="w-full"
                >
                  Seguir Comprando
                </Button>
              </div>

            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CarritoRepuestosExternos;